import { type SetStateAction, useCallback, useState } from 'react'

type ControllableStateParams<T> = {
  value?: T // controlled value
  defaultValue: T // initial value when uncontrolled
  onChange?: (v: T) => void // notify on changes (both modes)
}

export const useControllableState = <T>({
  value,
  defaultValue,
  onChange,
}: ControllableStateParams<T>) => {
  const isControlled = value !== undefined
  const [uncontrolled, setUncontrolled] = useState<T>(defaultValue)

  const current = isControlled ? value : uncontrolled

  const set = useCallback(
    (next: SetStateAction<T>) => {
      const nextValue =
        typeof next === 'function' ? (next as (prev: T) => T)(current) : next

      onChange?.(nextValue)

      if (!isControlled) {
        setUncontrolled(nextValue)
      }
    },
    [isControlled, current, onChange],
  )

  return [current, set] as const
}
